<template>
  <div class="claim-contacts-info">
    <div v-if="isLoading" class="claim-contacts-info__loading">
      <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
      <span class="text-muted">Loading contacts...</span>
    </div>

    <div v-else-if="!isLoaded" class="claim-contacts-info__empty">
      <button
        type="button"
        class="btn btn-sm btn-outline-primary"
        @click.stop="requestLoad"
      >
        Load contacts
      </button>
    </div>

    <template v-else>
    <div v-if="company" class="claim-contacts-info__row claim-contacts-info__company">
      <span class="claim-contacts-info__name">{{ company.name || 'Unknown company' }}</span>
      <a
        v-if="company.linkedin_url"
        :href="company.linkedin_url"
        target="_blank"
        rel="noopener noreferrer"
        class="claim-contacts-info__icon text-primary"
        title="LinkedIn found"
      >
        <i class="icon-linkedin color-ln"></i>
      </a>
      <i
        v-else-if="linkedinStatus(company) === 'missing'"
        class="icon-close claim-contacts-info__icon text-danger"
        title="LinkedIn not found"
      ></i>
      <i
        v-else
        class="icon-search claim-contacts-info__icon text-muted"
        title="LinkedIn not enriched yet"
      ></i>
    </div>

    <div
      v-for="(contact, index) in contacts"
      :key="contactKey(contact, index)"
      class="claim-contacts-info__row"
    >
      <i
        v-if="contact.is_meeting_contact"
        class="icon-calendar text-primary mr-1"
        title="Meeting contact"
      ></i>
      <span class="claim-contacts-info__name">{{ contact.name || contact.email || 'Unknown contact' }}</span>
      <small v-if="shouldShowEmail(contact)" class="claim-contacts-info__email">{{ contact.email }}</small>
      <a
        v-if="contact.linkedin_url"
        :href="contact.linkedin_url"
        target="_blank"
        rel="noopener noreferrer"
        class="claim-contacts-info__icon text-primary"
        title="LinkedIn found"
      >
        <i class="icon-linkedin color-ln"></i>
      </a>
      <i
        v-else-if="linkedinStatus(contact) === 'missing'"
        class="icon-close claim-contacts-info__icon text-danger"
        title="LinkedIn not found"
      ></i>
      <i
        v-else
        class="icon-search claim-contacts-info__icon text-muted"
        title="LinkedIn not enriched yet"
      ></i>
    </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ClaimContactsInformation',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    company() {
      return this.data && this.data.company ? this.data.company : null;
    },
    contacts() {
      const rawContacts = this.data && Array.isArray(this.data.contacts) ? this.data.contacts : [];
      const dedupedContacts = new Map();

      rawContacts.forEach((contact) => {
        const normalizedEmail = (contact.email || '').trim().toLowerCase();
        const normalizedId = contact.id !== undefined && contact.id !== null ? String(contact.id).trim() : '';
        const normalizedName = (contact.name || '').trim().toLowerCase();

        const dedupeKey = normalizedEmail || normalizedId || normalizedName;

        // If we cannot identify the contact, keep it as an independent row.
        if (!dedupeKey) {
          dedupedContacts.set(`fallback-${dedupedContacts.size}`, contact);
          return;
        }

        const existing = dedupedContacts.get(dedupeKey);
        if (!existing) {
          dedupedContacts.set(dedupeKey, { ...contact });
          return;
        }

        dedupedContacts.set(dedupeKey, {
          ...existing,
          ...contact,
          // Preserve meeting flag if any duplicated record has it.
          is_meeting_contact: Boolean(existing.is_meeting_contact || contact.is_meeting_contact),
          // Keep first non-empty values when the latest payload has blanks.
          name: contact.name || existing.name,
          email: contact.email || existing.email,
          linkedin_url: contact.linkedin_url || existing.linkedin_url,
          linkedin_status: contact.linkedin_status || existing.linkedin_status,
        });
      });

      return Array.from(dedupedContacts.values());
    },
    claimId() {
      return this.data && this.data.claim_id ? Number(this.data.claim_id) : null;
    },
    isLoading() {
      return !!(this.data && this.data.loading === true);
    },
    isLoaded() {
      return !!(this.data && this.data.loaded === true);
    },
  },
  methods: {
    requestLoad() {
      if (!this.claimId) return;
      this.$emit('load-contacts', this.claimId);
    },
    contactKey(contact, index) {
      return contact.email || contact.id || index;
    },
    shouldShowEmail(contact) {
      const name = (contact.name || '').trim().toLowerCase();
      const email = (contact.email || '').trim().toLowerCase();
      return email && name !== email;
    },
    linkedinStatus(item) {
      return item.linkedin_status || (item.linkedin_url ? 'found' : 'pending');
    },
  },
};
</script>

<style scoped>
.claim-contacts-info {
  min-width: 220px;
}

.claim-contacts-info__loading {
  display: flex;
  align-items: center;
  min-height: 24px;
}

.claim-contacts-info__empty {
  min-height: 24px;
  display: flex;
  align-items: center;
}

.claim-contacts-info__row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
  line-height: 1.25;
  margin-bottom: 4px;
}

.claim-contacts-info__company {
  font-weight: 600;
  margin-bottom: 6px;
}

.claim-contacts-info__name {
  color: #111827;
  min-width: 0;
}

.claim-contacts-info__email {
  color: #6b7280;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.claim-contacts-info__icon {
  margin-left: 4px;
}
</style>
